import React, { useCallback, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import styles from "./Style.module.css";
import { Typography } from "@mui/material";
import ShadowBox from "../../../../components/ShadowBox/ShadowBox";
import { removeUnderScore } from "../../../../helper/Helper";
import NoDataFound from "../NoDataFound/NoDataFound";
import { FileDownloadOutlined } from "@mui/icons-material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const CustomTooltip = ({ active, payload, label, colors }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className={styles.tooltipContainer}>
      <strong className={styles.tooltipDate}>{label}</strong>
      {payload.map((entry) => (
        <div key={entry.name} className={styles.tooltipItem}>
          <span
            className={styles.tooltipDot}
            style={{ backgroundColor: colors[entry.name] || entry.color }}
          ></span>
          {removeUnderScore(entry.name)}: <strong>{entry.value}</strong>
        </div>
      ))}
    </div>
  );
};

const CustomLegend = ({ colors }) => {
  return (
    <div className={styles.legendContainer}>
      {Object.keys(colors).map((key) => (
        <div key={key} className={styles.legendItem}>
          <span
            className={styles.legendDot}
            style={{ backgroundColor: colors[key] }}
          ></span>
          {removeUnderScore(key)}
        </div>
      ))}
    </div>
  );
};

const StackedBarChart = ({
  data = [],
  colors = {},
  dataKey = "",
  className = "",
  title = "Stack Bar",
  style = {},
  height = 250,
  isImgDownload = false,
  isExpand = false,
  onExpand,
  isExport,
  handleExport,
  downloadTitle = "",
}) => {
  const chartRef = useRef();
  const keys = Object.keys(data[0] || {}).filter((key) => key !== dataKey);
  const minChartWidth = data?.length * 50;

  const handleDownload = useCallback(() => {
    if (!chartRef.current) return;

    const svg = chartRef.current.container?.firstChild;
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const paddingTop = 40;
      const canvas = document.createElement("canvas");
      canvas.width = svg.clientWidth || 1200;
      canvas.height = (svg.clientHeight || 600) + paddingTop;
      const ctx = canvas.getContext("2d");

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "#636578";
      ctx.font = "bold 16px Arial";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(title || "Stack Bar", 10, 10);

      ctx.drawImage(img, 0, paddingTop);

      // Clean up
      URL.revokeObjectURL(url);

      const pngUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = `${title || "chart"}.png`;
      a.click();
    };
    img.src = url;
  }, [title]);

  return (
    <ShadowBox className={`${styles.wrapper} ${className}`} style={style}>
      <div className={styles.header}>
        <div className={styles.dFlex}>
          <Typography variant={"h5"} className={"heading"}>
            {title}
          </Typography>
          {isExpand && (
            <OpenInNewIcon
              onClick={onExpand}
              color="primary"
              size="small"
              className={styles.expandIcon}
            />
          )}
          {isImgDownload && (
            <FileDownloadOutlined
              onClick={handleDownload}
              color="primary"
              size="small"
              className={styles.downloadIcon}
            />
          )}
        </div>

        <div>
          {isExport && (
            <div className={styles.dFlex2}>
              <Typography variant="h5" className="heading">
                {downloadTitle}
              </Typography>
              <FileDownloadOutlined
                onClick={handleExport}
                color="primary"
                size="small"
                className={styles.expandIcon}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.dFlex}>
        <CustomLegend colors={colors} />
      </div>
      {data?.length > 0 ? (
        <>
          <div className={styles.scrollable}>
            <div style={{ minWidth: `${minChartWidth}px` }}>
              <ResponsiveContainer width="100%" height={height}>
                <BarChart
                  data={data}
                  ref={chartRef}
                  margin={{ top: 20, right: 30, left: 10, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#bbbbbb" />
                  <XAxis
                    dataKey={dataKey}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    dy={5}
                  />
                  <YAxis />
                  <Tooltip
                    cursor={false}
                    content={<CustomTooltip colors={colors} />}
                  />
                  {keys.map((key) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="a"
                      fill={colors[key] || "#ccc"}
                      barSize={30}
                      isAnimationActive={false}
                    >
                      <LabelList
                        dataKey={key}
                        content={({ x, y, width, height, value }) => {
                          if (!value) return null; // skip 0
                          return (
                            <text
                              x={x + width / 2}
                              y={y + height / 2}
                              fill="#000"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fontSize={12}
                              fontWeight="bold"
                            >
                              {value}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      ) : (
        <NoDataFound />
      )}
    </ShadowBox>
  );
};

export default StackedBarChart;
